import { useState, useEffect } from 'react';
import { useSEO, generateProductSchema, generateFAQSchema, generateWebPageSchema } from '../../utils/seo';

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Get site URL from environment variable
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://sona.conejox.com.es';

  // Add SEO and Schema.org
  useSEO({
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateWebPageSchema(siteUrl),
        generateProductSchema(siteUrl),
        generateFAQSchema()
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: '¿Hace mucho ruido?',
      answer: 'No. Su tecnología sónica es casi silenciosa, perfecta para tu privacidad e intimidad.'
    },
    {
      question: '¿Puedo usarlo en la ducha?',
      answer: 'Sí, es resistente al agua al 100%. Disfruta de momentos de placer en la bañera o la ducha sin preocupaciones.'
    },
    {
      question: '¿Es seguro para el cuerpo?',
      answer: 'Totalmente. Hecho con silicona médica premium, libre de ftalatos y completamente hipoalergénico.'
    },
    {
      question: '¿Cuánto dura la batería?',
      answer: 'Hasta 2 horas de placer continuo con una sola carga. Recargable por USB para tu comodidad.'
    }
  ];

  const benefits = [
    {
      icon: '✨',
      title: 'Pulsos sónicos revolucionarios',
      description: 'Estimula sin contacto directo para orgasmos más profundos y envolventes.'
    },
    {
      icon: '💎',
      title: 'Diseño sueco de lujo',
      description: 'Elegante, ergonómico y con silicona ultrasuave de grado médico.'
    },
    {
      icon: '🔇',
      title: 'Placer silencioso',
      description: 'Sonido mínimo para una experiencia íntima y completamente privada.'
    },
    {
      icon: '🌀',
      title: 'Tecnología Cruise Control',
      description: 'Ajusta la potencia automáticamente para no perder intensidad.'
    },
    {
      icon: '💧',
      title: '100% sumergible y recargable',
      description: 'Ideal para el baño o la ducha. Placer sin límites.'
    }
  ];

  const testimonials = [
    {
      text: 'Nunca había sentido algo tan intenso sin tocarme. Es una experiencia completamente diferente.',
      name: 'María L.',
      verified: true
    },
    {
      text: 'Es mi ritual personal. Me desconecta del estrés y me conecta conmigo misma.',
      name: 'Carmen R.',
      verified: true
    },
    {
      text: 'Discreto, suave y muy, muy potente. La mejor inversión en mi bienestar.',
      name: 'Laura S.',
      verified: true
    }
  ];

  const handleCTAClick = () => {
    window.open('https://amzn.to/48YcVuL', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-white via-pink-50/30 to-rose-50/40">
        <div className="absolute inset-0 overflow-hidden pointer-events-none will-change-transform">
          <div className="absolute top-20 right-20 w-96 h-96 bg-pink-200/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-40 right-40 w-80 h-80 bg-rose-200/15 rounded-full blur-3xl"></div>
          <div className="absolute top-60 right-60 w-64 h-64 bg-pink-300/10 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="text-left space-y-8">
              <h1 className="font-serif text-5xl lg:text-6xl xl:text-7xl font-semibold text-gray-900 leading-tight">
                LELO SONA: descubre el placer{' '}
                <span className="text-pink-600">sin límites</span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-gray-600 leading-relaxed font-light">
                LELO SONA 2 Cruise: la revolución en estimulación sónica que transforma tu intimidad
              </p>

              <button
                onClick={handleCTAClick}
                className="group inline-flex items-center gap-3 bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white font-semibold text-base uppercase tracking-wide px-12 py-5 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-pink-500/30 cursor-pointer whitespace-nowrap will-change-transform"
                aria-label="Comprar LELO SONA 2 Cruise"
              >
                Comprar ahora
                <i className="ri-arrow-right-up-line text-xl group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"></i>
              </button>
            </div>

            {/* Right Visual */}
            <div className="relative">
              <div className="relative z-10">
                <img
                  src="https://public.readdy.ai/ai/img_res/85d7d768a4df9eee839e00cdb32f3ce2.jpg"
                  alt="LELO SONA 2 Cruise - Estimulador clitoral de lujo"
                  className="w-full h-auto object-contain drop-shadow-2xl"
                  width="600"
                  height="700"
                  loading="eager"
                  fetchpriority="high"
                />
              </div>
              <div className="absolute top-8 right-8 bg-white/90 backdrop-blur-sm rounded-full px-6 py-3 shadow-lg">
                <div className="flex items-center gap-2">
                  <i className="ri-sound-module-line text-pink-600 text-xl" aria-hidden="true"></i>
                  <span className="text-sm font-semibold text-gray-800">Tecnología Sónica</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Introduction */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <p className="text-2xl lg:text-3xl text-gray-800 leading-relaxed">
            SONA 2 Cruise no es un vibrador. Es una <strong className="text-pink-600 font-bold">revolución en placer</strong> que utiliza ondas sónicas para estimular todo el clítoris, no solo la punta. Diseño sueco de <strong className="text-pink-600 font-bold">lujo absoluto</strong> para mujeres que buscan lo mejor.
          </p>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-24 bg-gradient-to-b from-white to-pink-50/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl lg:text-5xl font-semibold text-gray-900 mb-4">
              Descubre cómo funciona
            </h2>
            <p className="text-lg text-gray-600">Mira SONA 2 Cruise en acción</p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-gray-900" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/yqNtK7nGWCw"
                title="LELO SONA 2 Cruise - Video del producto"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
                aria-label="Video demostrativo de LELO SONA 2 Cruise"
              ></iframe>
            </div>

            <div className="mt-8 text-center">
              <p className="text-base text-gray-600 mb-6">
                Experimenta la tecnología sónica revolucionaria que está transformando el placer femenino
              </p>
              <button
                onClick={handleCTAClick}
                className="group inline-flex items-center gap-3 bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white font-semibold text-base uppercase tracking-wide px-10 py-4 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-pink-500/30 cursor-pointer whitespace-nowrap will-change-transform"
                aria-label="Comprar LELO SONA 2 Cruise"
              >
                Comprar ahora
                <i className="ri-arrow-right-up-line text-xl group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-gradient-to-b from-pink-50/40 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl lg:text-5xl font-semibold text-gray-900 mb-4">
              Por qué amarás SONA 2 Cruise
            </h2>
            <p className="text-lg text-gray-600">Cinco razones que lo hacen único</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 max-w-7xl mx-auto">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-2xl hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer will-change-transform"
              >
                <div className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-4xl" role="img" aria-label={benefit.title}>{benefit.icon}</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 text-center leading-snug">
                  {benefit.title}
                </h3>
                <p className="text-sm text-gray-600 text-center leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl lg:text-5xl font-semibold text-gray-900 mb-4">
              Experiencias reales
            </h2>
            <p className="text-lg text-gray-600">Lo que dicen quienes ya lo probaron</p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-pink-50/50 rounded-3xl p-8 relative"
              >
                <div className="text-pink-600 text-6xl font-serif absolute top-6 left-6 opacity-30" aria-hidden="true">"</div>
                <p className="text-lg text-gray-800 italic leading-relaxed mb-6 relative z-10 pt-8">
                  {testimonial.text}
                </p>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <p className="font-bold text-gray-900 text-base">{testimonial.name}</p>
                  </div>
                  {testimonial.verified && (
                    <div className="bg-green-100 rounded-full px-3 py-1 flex items-center gap-1">
                      <span className="text-xs" role="img" aria-label="Verificado">⭐</span>
                      <span className="text-xs font-semibold text-green-800">Verificado</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gradient-to-b from-white to-pink-50/40">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl lg:text-5xl font-semibold text-gray-900">
              Preguntas frecuentes
            </h2>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl overflow-hidden border-2 border-pink-100">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-8 py-6 flex items-center justify-between hover:bg-pink-50/30 transition-colors cursor-pointer"
                  aria-expanded={openFaq === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-2xl" role="img" aria-hidden="true">❓</span>
                    <span className="text-lg font-semibold text-gray-900 text-left">
                      {faq.question}
                    </span>
                  </div>
                  <i
                    className={`ri-${openFaq === index ? 'subtract' : 'add'}-line text-2xl text-pink-600 transition-transform duration-300 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                    aria-hidden="true"
                  ></i>
                </button>
                
                {openFaq === index && (
                  <div id={`faq-answer-${index}`} className="px-8 py-6 bg-pink-50/30 border-t border-pink-100">
                    <div className="flex items-start gap-4">
                      <span className="text-xl mt-1" role="img" aria-hidden="true">✅</span>
                      <p className="text-base text-gray-800 leading-relaxed flex-1">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=soft%20blurred%20abstract%20background%20with%20pink%20rose%20tones%20elegant%20feminine%20aesthetic%20luxury%20wellness%20intimate%20atmosphere%20dreamy%20bokeh%20effect%20gentle%20lighting%20premium%20quality%20sensual%20mood%20sophisticated%20design&width=1920&height=800&seq=cta-bg-001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
            width="1920"
            height="800"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-pink-600/70 via-pink-500/60 to-rose-600/70"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10 text-center">
          <h2 className="font-serif text-4xl lg:text-6xl font-semibold text-white mb-6 leading-tight drop-shadow-lg">
            Llévate el placer a casa hoy
          </h2>
          <p className="text-xl lg:text-2xl text-white/90 mb-10 max-w-3xl mx-auto">
            Invierte en tu bienestar. Descubre una nueva dimensión de placer con SONA 2 Cruise.
          </p>

          <button
            onClick={handleCTAClick}
            className="group inline-flex items-center gap-3 bg-white hover:bg-pink-600 text-pink-600 hover:text-white font-semibold text-base uppercase tracking-wide px-12 py-5 rounded-full transition-all duration-300 hover:scale-105 shadow-2xl cursor-pointer whitespace-nowrap will-change-transform"
            aria-label="Comprar LELO SONA 2 Cruise"
          >
            Comprar ahora
            <i className="ri-arrow-right-up-line text-xl group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" aria-hidden="true"></i>
          </button>

          <div className="flex items-center justify-center gap-8 mt-12 flex-wrap">
            <div className="bg-white/20 backdrop-blur-sm rounded-full px-6 py-3 flex items-center gap-2">
              <i className="ri-shield-check-line text-white text-xl" aria-hidden="true"></i>
              <span className="text-white text-sm font-medium">Envío discreto</span>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-full px-6 py-3 flex items-center gap-2">
              <i className="ri-award-line text-white text-xl" aria-hidden="true"></i>
              <span className="text-white text-sm font-medium">Garantía 1 año</span>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-full px-6 py-3 flex items-center gap-2">
              <i className="ri-lock-line text-white text-xl" aria-hidden="true"></i>
              <span className="text-white text-sm font-medium">Pago seguro</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-16">
        <div className="container mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <a href="https://conejox.com.es/" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
              <img 
                src="https://public.readdy.ai/ai/img_res/57c2a0e1-0595-4fb0-bf2e-977ac727a901.png" 
                alt="Conejox Logo" 
                className="h-12 w-auto hover:opacity-80 transition-opacity"
                width="48"
                height="48"
                loading="lazy"
              />
            </a>
          </div>
          
          <div className="flex items-center justify-center gap-6 mb-8">
            <a href="#" className="w-10 h-10 rounded-full border-2 border-gray-600 hover:border-pink-500 flex items-center justify-center transition-colors cursor-pointer" aria-label="Instagram">
              <i className="ri-instagram-line text-gray-400 hover:text-pink-500 text-xl" aria-hidden="true"></i>
            </a>
            <a href="#" className="w-10 h-10 rounded-full border-2 border-gray-600 hover:border-pink-500 flex items-center justify-center transition-colors cursor-pointer" aria-label="Facebook">
              <i className="ri-facebook-circle-line text-gray-400 hover:text-pink-500 text-xl" aria-hidden="true"></i>
            </a>
            <a href="#" className="w-10 h-10 rounded-full border-2 border-gray-600 hover:border-pink-500 flex items-center justify-center transition-colors cursor-pointer" aria-label="Twitter">
              <i className="ri-twitter-x-line text-gray-400 hover:text-pink-500 text-xl" aria-hidden="true"></i>
            </a>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border-2 border-gray-600 hover:border-pink-500 flex items-center justify-center transition-colors cursor-pointer" aria-label="Sitio web">
              <i className="ri-global-line text-gray-400 hover:text-pink-500 text-xl" aria-hidden="true"></i>
            </a>
          </div>

          <div className="w-32 h-px bg-gray-700 mx-auto my-8"></div>
          
          <div className="max-w-4xl mx-auto">
            <p className="text-xs text-gray-400 leading-relaxed mb-3">
              © 2025 Conejox.com.es – Todos los derechos reservados.
            </p>
            <p className="text-xs text-gray-400 leading-relaxed mb-3">
              Esta página contiene enlaces de afiliado de Amazon. Si compras a través de ellos, podemos recibir una comisión sin coste adicional para ti.
            </p>
            <p className="text-xs text-gray-400 leading-relaxed mb-6">
              Contenido solo para mayores de 18 años. Navegación segura y sin cookies publicitarias invasivas.
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <a href="https://conejox.com.es/aviso-legal/" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 hover:text-pink-500 transition-colors cursor-pointer">
                Aviso Legal
              </a>
              <span className="text-gray-600">|</span>
              <a href="https://conejox.com.es/politica-de-privacidad/" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 hover:text-pink-500 transition-colors cursor-pointer">
                Política de Privacidad
              </a>
              <span className="text-gray-600">|</span>
              <a href="https://conejox.com.es/politica-de-cookies/" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 hover:text-pink-500 transition-colors cursor-pointer">
                Política de Cookies
              </a>
              <span className="text-gray-600">|</span>
              <a href="mailto:contacto@conejox.com.es" className="text-xs text-gray-400 hover:text-pink-500 transition-colors cursor-pointer">
                Contacto
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 left-8 w-12 h-12 bg-pink-600 hover:bg-pink-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 flex items-center justify-center cursor-pointer z-50 will-change-transform"
          aria-label="Volver arriba"
        >
          <i className="ri-arrow-up-line text-xl" aria-hidden="true"></i>
        </button>
      )}
    </div>
  );
}
